/**
 * Login page for the standalone admin panel — password login only
 * (no public registration, no "skip / continue without account").
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Mail, Lock, ShieldCheck, ArrowRight } from "lucide-react";

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, isAuthed } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (isAuthed) navigate("/", { replace: true });
  }, [isAuthed, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-midnight via-surface to-midnight" data-testid="auth-login-page">
      <div className="w-full max-w-md card-celestial rounded-2xl p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="w-6 h-6 text-glow-light" />
            <h1 className="text-2xl font-display font-bold text-celestial-text">Admin Panel</h1>
          </div>
          <p className="text-sm text-celestial-muted">Pocket TCG Hub — admin access only</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-login-form">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-celestial-muted">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-celestial-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 focus:outline-none focus:border-glow-mid/50"
                data-testid="auth-email-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-celestial-muted">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-celestial-muted" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
                minLength={6}
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-surface/50 border border-celestial-border/20 text-celestial-text placeholder:text-celestial-muted/40 focus:outline-none focus:border-glow-mid/50"
                data-testid="auth-password-input"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-xl px-3 py-2" data-testid="auth-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="w-full btn-celestial py-2.5 rounded-xl font-medium flex items-center justify-center gap-2"
            data-testid="auth-login-submit"
          >
            {busy ? "…" : "Log In"}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AuthPage;
